export function removeOutliersFBAData(data, trace, lofK, lofCutoff, mmrK, mmrCutoff) {
  try {

    if (!data || data.length === 0) return [];

    let outliers = [];

    let lofOutliers = [];
    let mmrOutliers = [];

    if (lofCutoff > 0) {
      lofOutliers = calculateLOF(data, trace, lofK, lofCutoff);
    };
    if (mmrCutoff > 0) {
      mmrOutliers = calculateMovingMedianRatio(data, trace, mmrK, mmrCutoff);
    };

    lofOutliers?.forEach(m => {
      if (!outliers.find(o => o.days === m.days)) {
        outliers.push(m);
      };
    });

    mmrOutliers?.forEach(m => {
      if (!outliers.find(o => o.days === m.days)) {
        outliers.push(m);
      };
    });

    outliers = outliers.sort((a, b) => a.days - b.days);

    // find index of each outlier point
    const outlierIndices = [];

    data.forEach((d, i) => {
      if (outliers.find(o => o.days === d.days) && i > 0 && i < data.length - 1) {
        outlierIndices.push(i);
      };
    });

    const newData = interpolateSpecificPoints(data, outlierIndices, trace);

    return newData?.map(d => d[trace]);

  } catch (error) {
    console.log(error);
    return [];
  };
};

function calculateLOF(data, trace, k, lofCutoff) {
  try {
    const nRows = data.length;

    let distances = [];

    for (let i = 0; i < nRows; i++) { // for every point

      // calculate distances to every other point
      let localDistances = [];

      const localValue = data[i]?.[trace];
      const localDays = data[i]?.days;

      for (let j = 0; j < nRows; j++) {
        const distance = Math.sqrt(Math.pow(Math.abs(data[j]?.[trace] - localValue), 2) + Math.pow(Math.abs(data[j]?.days - localDays), 2)); // Euclidian distance
        distance > 0 && localDistances.push({ index: j, d: distance});
      };

      localDistances.sort((a, b) => a.d - b.d); // sort the local distances array in ascending order

      const kNeighbors = localDistances.slice(0, k); // take k neighbors

      distances.push(kNeighbors); // each row of distances array corresponds to a raw data point
    };

    // calculate lrd
    let lrdArray = [];
    for (let i = 0; i < nRows; i++) { // for every point

      // calculate reachability-distance
      let rdArray = [];
      for (let j = 0; j < k; j++) { // for every neighbor
        const indexNeighbor = distances[i][j]?.index; // index of the current neighbor
        const kdNeighbor = distances[indexNeighbor]?.slice(-1)?.[0]?.d; // k-distance of the current neighbor
        const rd = Math.max(distances[i][j]?.d, kdNeighbor); // reachability-distance from current point to current neighbor
        rdArray.push(rd);
      };

      if (rdArray.length > 0) {
        const lrd = 1 / (rdArray.reduce((tot, curr) => tot + curr) / rdArray.length);
        lrdArray.push(lrd);
      };
    };

    // calculate LOF
    let lofArray = [];
    for (let i = 0; i < nRows; i++) { // for every point
    
      let neigborsLRD = 0;

      for (let j = 0; j < k; j++) { // for every neighbor
        const indexNeighbor = distances[i][j]?.index; // index of the current neighbor
        neigborsLRD += lrdArray[indexNeighbor];
      };

      const LOF = (neigborsLRD / k) / lrdArray[i];
      lofArray.push({days: data[i]?.days, lof: LOF});  
    };

    const sortedLOF = lofArray?.map(l => l.lof)?.sort((a,b) => b - a);

    const quartile = sortedLOF?.[Math.floor(sortedLOF?.length * lofCutoff)];

    const outliers = lofArray.filter((d, i) => d.lof >= quartile);

    return outliers;

  } catch (error) {
    console.log(error);
  };
};

function calculateMovingMedianRatio(data, trace, k, mmrCutoff) {
  try {
    
    if (!data || !data.length > 0) return;

    const nRows = data.length;
    const values = data.map(d => d[trace]);

    let mmrArray = [];
    let outliers = [];

    for (let i = 0; i < nRows; i++) {
      const localRate = data[i]?.[trace];
      const nextKMedian = getMedian(values.slice(i, i + k));
      const mmr = nextKMedian > 0 ? localRate / nextKMedian : 0;

      mmrArray.push({days: data[i]?.days, mmr});
    };

    const stdDev = getStandardDeviation(mmrArray.map(m => m.mmr));
    const mean = mmrArray.map(m => m.mmr).reduce((a, b) => a + b) / mmrArray.length;

    outliers = mmrArray.filter(m => m.mmr < mean - mmrCutoff * stdDev || m.mmr > mean + mmrCutoff * stdDev);

    return outliers;

  } catch (error) {
    console.log(error);
  };
};

function getMedian(array){
  if (array?.length === 0) return;

  let values = array.sort((a,b) => a - b);
  const half = Math.floor(values.length / 2);
  
  if (values.length % 2) {
    return values[half];
  };
  return (values[half - 1] + values[half]) / 2.0;
};

function getStandardDeviation(array) {
  if (array?.length === 0) return;

  const n = array.length;
  const mean = array.reduce((a, b) => a + b) / n;
  return Math.sqrt(array.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / n);
};

function interpolateSpecificPoints(rawData, indices, trace) {
  try {
  
    const interpolatedData = [...rawData];

    let prevIndex = indices[0];
    let prevData = rawData[prevIndex - 1];

    for (let i = 1; i < indices.length; i++) {
      const currentIndex = indices[i];

      if (currentIndex - prevIndex > 1) {
        const nextData = rawData[currentIndex + 1];
        const valueDiff = (nextData[trace] - prevData[trace]) / (currentIndex - prevIndex + 2);

        for (let j = prevIndex + 1; j < currentIndex; j++) {
          const interpolatedValue = prevData[trace] + valueDiff * (j - prevIndex);
          interpolatedData[j] = {...interpolatedData[j], [trace]: interpolatedValue };
        };
      };

      prevIndex = currentIndex;
      prevData = rawData[currentIndex - 1];
    };

    return interpolatedData;

  } catch (error) {
    console.log(error);
    return rawData;
  };
};

// function interpolateData(data) {
//   try {
//     if (!data || data?.length === 0) return;

//     let newData = [...data];

//     data.every((d,i) => {
//       if (d.monthsOnline > newData[i-1]?.monthsOnline + 1) { // if missing 1 or more months
//         const numPoints = d?.monthsOnline - data?.[i - 1]?.monthsOnline; // find number of months missing
//         const monthsOnline = data?.[i - 1]?.monthsOnline + 1;
//         const ratio = 1 / numPoints;
//         const prevVolume = data?.[i - 1]?.volume;
//         const nextVolume = d?.volume;
//         const volume = lerp(prevVolume, nextVolume, ratio);
//         newData.splice(i, 0, {monthsOnline, volume});
//         return false;
//       } else {
//         return true;
//       };
//     });
//     return newData;
//   } catch (error) {
//     console.log(error);
//   };
// };

// function lerp (x, y, a) {
//   return x * (1 - a) + y * a;
// };